import React, { useRef, useState } from 'react';
import { RiUploadCloud2Line } from "react-icons/ri";
import { VscSparkleFilled } from "react-icons/vsc";
import { LuGem, LuScissors } from 'react-icons/lu';
import { FaRegClock, FaCheckCircle } from 'react-icons/fa';
import { FaInstagram, FaLinkedinIn, FaFacebookF, FaTwitter } from 'react-icons/fa';
import './ai_highlights.scss';
import fire from "../../assets/images/icons/fire.svg";
import aiPanelRight from "../../assets/images/ai-panel-right.png";

const MAX_FILE_SIZE_BYTES = 1024 * 1024 * 1024; // 1 GB

const reasonCards = [
  {
    icon: FaRegClock,
    title: 'Save time',
    description:
      "Who has hours to manually find highlights? Criccenter does it in seconds. That's 90% less editing time for you.",
  },
  {
    icon: LuGem,
    title: 'Turn footage into gold.',
    description:
      'Our highlight video maker finds the best bits. It also stitches them together to make you look like a pro.',
  },
  {
    icon: LuScissors,
    title: 'No editing skills required',
    description:
      'You can make highlight videos. Clip Anything lets you prompt the AI to grab exactly what you want. No timeline scrubbing needed.',
  },
];

const processSteps = [
  'Import your footage',
  'Use Clip Anything if you want, to guide our AI curation',
  'Let our AI work its magic',
  'Tweak the result if needed.',
  'Create your highlight video',
];

const AiHighlightsPage: React.FC = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [isDragging, setIsDragging] = useState(false);

  const validateFile = (file: File) => {
    const isMp4File = file.type === 'video/mp4' || file.name.toLowerCase().endsWith('.mp4');

    if (!isMp4File) {
      setErrorMessage("Oops! That file type isn't supported. Please upload an MP4.");
      return false;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      setErrorMessage('Oops! File size exceeds 1 GB. Please upload a smaller file.');
      return false;
    }

    setErrorMessage('');
    return true;
  };

  const handleFileSelect = (file?: File | null) => {
    if (!file) {
      return;
    }

    validateFile(file);
  };

  const onFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleFileSelect(event.target.files?.[0]);
  };

  const onDrop = (event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    setIsDragging(false);
    handleFileSelect(event.dataTransfer.files?.[0]);
  };

  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="ai-highlights-page">
      <section className="ai-highlights-hero">
        <div className="ai-highlights-hero-content">
          <div className="ai-highlights-badge">
            <img className="fire-icon" src={fire} alt="fire-icon" /> AI HIGHLIGHTS
          </div>
          <h1 className="ai-highlights-title">
            <span>Turn your Cricket Matches into</span>
            <br />
            <span className="light">Thrilling Highlights</span>
          </h1>
        </div>
        <div className="upload-dropzone-container">
          <label
            className={`upload-dropzone ${isDragging ? 'dragging' : ''}`}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={onDrop}
          >
            <RiUploadCloud2Line size={24} color="#713DFF" />
            <p>Click or drag your video file here</p>
            <input
              ref={fileInputRef}
              type="file"
              accept="video/mp4"
              onChange={onFileInputChange}
            />
          </label>
          <div className="upload-meta">
            <p className="upload-support-text">Supported format: <span className="upload-support-text-highlight">MP4 only</span></p>
            <p className="upload-size-text">Maximum file size: <span className="upload-support-text-highlight">1 GB</span></p>
          </div>
        </div>

        <button type="button" className="upload-btn" onClick={openFilePicker}>
          <VscSparkleFilled size={24} color="#ffffff" style={{ transform: 'rotate(90deg)' }} />
          Upload Your Video
        </button>

        {errorMessage ? <p className="upload-error">{errorMessage}</p> : null}
      </section>

      <div className="ai-section-divider" />

      <section className="ai-reasons-section">
        <div className="ai-reasons-socials" aria-hidden="true">
          <span><FaInstagram /></span>
          <span><FaLinkedinIn /></span>
          <span><FaFacebookF /></span>
          <span><FaTwitter /></span>
        </div>

        <div className="ai-highlights-hero-content">
          <div className="ai-highlights-badge">
            <img className="fire-icon" src={fire} alt="fire-icon" />REASONS
          </div>

          <h2 className="ai-process-title">
            <span>3 Reasons You Need an</span>
            <br />
            <span className="light">AI Highlight Maker</span>
          </h2>
        </div>

        <div className="ai-reasons-cards">
          {reasonCards.map((reason) => {
            const Icon = reason.icon;
            return (
              <article key={reason.title} className="ai-reason-card">
                <span className="ai-reason-icon">
                  <Icon />
                </span>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <h3>{reason.title}</h3>
                  <p>{reason.description}</p>
                </div>
              </article>
            );
          })}
        </div>

        <div className="ai-section-divider" />
      </section>

      <section className="ai-process-section">
        <div className="ai-highlights-hero-content">
          <div className="ai-highlights-badge">
            <img className="fire-icon" src={fire} alt="fire-icon" />PROCESS
          </div>

          <h2 className="ai-process-title">
            <span>How to create a</span>
            <br />
            <span className="gradient-text">Highlight Reel</span> video
          </h2>
        </div>

        <div className="ai-process-panel">
          <ul className="ai-process-list">
            {processSteps.map((step) => (
              <li key={step}>
                <FaCheckCircle />
                <span>{step}</span>
              </li>
            ))}
          </ul>

          <div className="ai-process-visual" aria-hidden="true">
            <img src={aiPanelRight} alt="" />
          </div>
        </div>
      </section>
    </div>
  );
};

export default AiHighlightsPage;

